import React, { useState } from 'react';
import { Settings, Eye, Type, Zap, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useAccessibility, FontSize, Contrast } from '@/hooks/useAccessibility';
import { useTheme, Theme } from '@/hooks/useTheme';

export const AccessibilityPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { settings, updateSettings, resetSettings, canPersist } = useAccessibility();
  const { theme, setTheme, canPersist: canPersistTheme } = useTheme();

  const fontSizeOptions = [
    { value: 'small' as FontSize, label: 'Klein' },
    { value: 'medium' as FontSize, label: 'Normal' },
    { value: 'large' as FontSize, label: 'Groß' },
    { value: 'extra-large' as FontSize, label: 'Sehr groß' },
  ];

  const themeOptions = [
    { value: 'light' as Theme, label: 'Hell' },
    { value: 'dark' as Theme, label: 'Dunkel' },
    { value: 'system' as Theme, label: 'System' },
  ];

  const contrastOptions = [
    { value: 'normal' as Contrast, label: 'Normal' },
    { value: 'high' as Contrast, label: 'Hoch' },
  ];

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="fixed bottom-4 right-4 z-50 h-12 w-12 rounded-full shadow-lg"
          aria-label="Barrierefreiheit-Einstellungen öffnen"
        >
          <Settings className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Barrierefreiheit
          </SheetTitle>
          <SheetDescription>
            Passen Sie die Anzeige an Ihre Bedürfnisse an. {!canPersist && 'Aktivieren Sie funktionale Cookies, um Einstellungen zu speichern.'}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 py-6">
          {/* Theme Selection */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Zap className="h-4 w-4" />
                Erscheinungsbild
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="theme-select">Farbschema</Label>
                <Select
                  value={theme}
                  onValueChange={(value: Theme) => setTheme(value)}
                >
                  <SelectTrigger id="theme-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {themeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!canPersistTheme && (
                  <p className="text-xs text-muted-foreground">
                    Einstellung wird nicht gespeichert (funktionale Cookies deaktiviert)
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Text and Display */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Type className="h-4 w-4" />
                Text & Anzeige
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="font-size-select">Schriftgröße</Label>
                <Select
                  value={settings.fontSize}
                  onValueChange={(value: FontSize) => updateSettings({ fontSize: value })}
                >
                  <SelectTrigger id="font-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {fontSizeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contrast-select">Kontrast</Label>
                <Select
                  value={settings.contrast}
                  onValueChange={(value: Contrast) => updateSettings({ contrast: value })}
                >
                  <SelectTrigger id="contrast-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {contrastOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="high-readability" className="text-sm font-normal">
                  Verbesserte Lesbarkeit
                </Label>
                <Switch
                  id="high-readability"
                  checked={settings.highReadability}
                  onCheckedChange={(checked) => updateSettings({ highReadability: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="reduced-motion" className="text-sm font-normal">
                  Bewegungen reduzieren
                </Label>
                <Switch
                  id="reduced-motion"
                  checked={settings.reducedMotion}
                  onCheckedChange={(checked) => updateSettings({ reducedMotion: checked })}
                />
              </div>

              {!canPersist && (
                <p className="text-xs text-muted-foreground">
                  Einstellungen werden nicht gespeichert (funktionale Cookies deaktiviert)
                </p>
              )}
            </CardContent>
          </Card>

          {/* Reset Button */}
          <Button
            variant="outline"
            onClick={resetSettings}
            className="w-full"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Alle Einstellungen zurücksetzen
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};